<template>
  <div class="container case-environment">
    <div v-if="state.caseConf[locationKey]" class="container preview-container">
      <VueSlickCarousel v-bind="slickOptions">
        <div v-for="environment in environments" :key="environment.id" class="environment-slide">
          <img aria-label="Environment image" class="environment-picture" :src="pictureFor(environment)">
        </div>
      </VueSlickCarousel>
      <md-button v-if="!required" :disabled="readonly" class="md-icon-button md-primary md-raised blue-6 bg remove-button" @click="updateLocation(null)">
        <md-icon class="">remove</md-icon>
      </md-button>
    </div>
    <div v-else class="container">
      <div class="case-environment-add">
        <md-button :disabled="readonly" class="md-primary md-raised blue-6 bg add-button" @click="setInitialEnvironment">
          <md-icon class="">add</md-icon>
        </md-button>
        <md-tooltip md-direction="bottom" md-delay="300">
          Add a scene that will be accessible in the simulation through the Transfer button
        </md-tooltip>
        <label>Add a Scene</label>
      </div>
    </div>
    <md-field class="environment-select" :class="{unselected: selectIndex === null}">
      <help-tooltip v-if="sceneHasBedRail" type="info">
        The selected scene uses a hospital bed with a railing that can be raised or lowered by the user.
      </help-tooltip>
      <md-select 
        ref="environmentSelect"
        v-model="selectIndex"
        aria-label="Environment select" 
        :disabled="readonly"
        placeholder="Add a Scene"
        @md-selected="onSelectChange"
      >
        <div 
          v-for="(environment, index) in environments" 
          :key="environment.id" 
          class="option-wrapper"
        >
          <img 
            :src="thumbnailFor(environment)" 
            class="environment-thumbnail"
            :class="{ 'environment-thumbnail-selected': index === selectIndex }"
            @click="onThumbnailClick(index)"
          >
          <md-option 
            :value="index" 
            class="environment-text"
          >
            {{ environment.name[caseConf.language] }}
          </md-option>
        </div>
      </md-select>
    </md-field>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import pick  from 'lodash/pick';
import assign from 'lodash/assign';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';

import HelpTooltip from '~lib/common/components/HelpTooltip';

export default {
  name: 'case-environments',
  components: {
    VueSlickCarousel,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    environments: {
      type: Array,
      default: () => [],
    },
    locationKey: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    mounted: {
      type: Boolean,
      default: true,
    },
    sceneSelectEventName: {
      type: String,
      default: 'scene_select'
    },
  },
  data() {
    return {
      selectIndex: this.initialEnvironmentIndex(),
      slickOptions: {
        centerMode: false,
        slidesToShow: 1,
        focusOnSelect: false,
        draggable: false,
        arrows: false,
        initialSlide: this.initialEnvironmentIndex(),
        lazyLoad: 'ondemand',
      },
      state: {
        caseConf: {},
      },
    };
  },
  computed: {
    defaults() {
      return {
        [this.locationKey]: get(this.caseConf, this.locationKey)
          || (this.required && this.environments[0]),
      };
    },
    sceneHasBedRail() {
      if (!this.caseConf[this.locationKey]) {
        return false;
      }
      return ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'].includes(this.caseConf[this.locationKey].key);
    }
  },
  watch: {
    selectIndex(newIndex) {
      this.slickOptions.initialSlide = newIndex;
      if (this.$refs.slick) {
        this.$refs.slick.goTo(newIndex);
      }
    },
  },
  created() {
    this.state.caseConf = assign({}, this.defaults, pick(this.caseConf, this.locationKey));
    if (this.required && !this.caseConf[this.locationKey]) {
      this.updateLocation(this.environments[0]);
    }
  },
  methods: {
    pictureFor(environment) {
      return get(environment, 'picture.url', 'http://icons.iconarchive.com/icons/papirus-team/papirus-status/512/image-missing-icon.png');
    },
    thumbnailFor(environment) {
      const baseLocation = this.pictureFor(environment);
      const parts = baseLocation.split('/');
      return parts.slice(0, -1).join('/') + '/thumbs/' + parts.slice(-1);
    },
    initialEnvironmentIndex() {
      if (!this.caseConf) {
        return null;
      }
      const location = this.caseConf[this.locationKey];
      if (!location) {
        return null;
      }
      return findIndex(this.environments, e => e.id === location.id);
    },
    setInitialEnvironment() {
      this.updateLocation(this.environments[0]);
    },
    updateLocation(environment) {
      // eslint-disable-next-line no-underscore-dangle
      this.state.caseConf[this.locationKey] = environment;
      this.selectIndex = environment
        ? findIndex(this.environments, e => e.id === environment.id)
        : null;
      this.emitUpdate();
      if (environment) {
        this.$analytics.event('Authoring Tool', this.sceneSelectEventName, environment.name.en);
      }
    },
    emitUpdate() {
      this.$emit('update', this.state.caseConf);
    },
    onSelectChange(index) {
      this.updateLocation(this.environments[index]);
    },
    onThumbnailClick(index) {
      if (index === null) {
        return;
      }
      // Hack to get the menu to close when clicking on the thumbnail image
      this.$refs.environmentSelect.$refs.menu.MdMenu.active = false;
      this.onSelectChange(index);
    }
  },
};
</script>

<style scoped>
.preview-container {
  position: relative;
}
.case-environment {
  padding: 8px 0;
}
.case-environment-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 0;
  position: relative;
  padding-top: 100%;
}
.case-environment-add .md-button {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  margin: 0;
}
.case-environment-add label {
  margin-top: 16px;    
  line-height: 32px;
}

.remove-button {
  position: absolute;
  top: 0;
  left: 0;
  margin-left: 0;
}

.environment-slide {
  text-align: center;
  height: 0;
  position: relative;
  padding-top: 100%;
}

.environment-picture {
  margin: auto;
  max-height: 100%;
  cursor: pointer;    
  position: absolute;
  top: 0;
  object-fit: cover;
  height: 100%;
}
.help-tooltip {
  margin-right: 5px;
}

.environment-select {
  margin-top: 10px;
}

/* Unfortunately there is a small margin
occurring in the slick slider that I can't
locate, so to align the selects, we give the
unselected select some extra margin */
.environment-select.unselected {
  margin-top: 15px;
}

.navigation-arrows {
  padding-top: 5px;
  display: flex;
  justify-content: center;
}

.option-wrapper {
  display: flex;
  flex-direction: row;
  cursor: pointer;
}

.environment-thumbnail {
  width: 64px;
  height: 64px;
}

.environment-thumbnail-selected {
  border: 3px solid #0C4E86;
}

.environment-text {
  flex: 1;
}

.add-button:not(.md-icon-button) {
  border-radius: 0px;
}
</style>
